import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, AreaChart, Area, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "../contexts/AuthContext";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const COLORS = ["#1a9e72", "#185fa5", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4", "#22c55e"];

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtShort(n) { if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + "k"; return String(n || 0); }
function pct(n) { return ((n || 0) * 100).toFixed(1) + "%"; }

function KPICard({ label, value, sub, color, trend }) {
  return (
    <div style={{ ...S.kpi, minWidth: 150 }}>
      <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#111827" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: trend === "up" ? "#1a9e72" : trend === "down" ? "#ef4444" : "#4b5563", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Analytics({ currentCompanyId, currentSociete, companies = [] }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("kpis");
  const [year, setYear] = useState(new Date().getFullYear());
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(false);

  const call = useCallback(async (path) => {
    const res = await fetch(path, { headers: { Authorization: "Bearer " + getToken() } });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const [chartData, setChartData] = useState(null);

  const loadDash = useCallback(async () => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      // Historique réel (CA encaissé + dépenses réelles, 6 derniers mois) —
      // même endpoint que Dashboard.jsx/Forecast.jsx, remplace la fabrication
      // Math.random() utilisée jusqu'ici en repli si dashData.monthly_revenue
      // était absent (toujours le cas, ce champ n'existe pas côté backend).
      const [dash, chart] = await Promise.all([
        call(`/api/dashboard?company_id=${currentCompanyId}`),
        call(`/api/dashboard/chart/${currentCompanyId}`).catch(() => null),
      ]);
      setDashData(dash);
      setChartData(chart);
    } catch { } finally { setLoading(false); }
  }, [currentCompanyId, year, call]);

  useEffect(() => { loadDash(); }, [loadDash]);

  // Monthly revenue chart — données réelles (GET /api/dashboard/chart/:companyId,
  // même source que Dashboard.jsx/Forecast.jsx). L'ancien repli Math.random()
  // s'activait systématiquement (dashData.monthly_revenue n'existe pas côté
  // backend) — remplacé par le vrai historique, jamais par une fabrication.
  const monthlyRevenue = (chartData || []).map(d => ({ month: d.m, ca: d.ca, charges: d.dep }));
  const revenueWithProfit = monthlyRevenue.map(d => ({ ...d, resultat: (d.ca || 0) - (d.charges || 0) }));

  // KPI derivations — trouvé en creusant les messages "Insights IA" : ca,
  // charges, impayés et dso lisaient des champs qui n'existent pas côté
  // backend (revenue_ht, total_expenses, unpaid_invoices,
  // average_payment_delay) — toujours à 0 ou au repli fixe depuis le début,
  // silencieusement. Vérifié contre server/routes/dashboard.js : les vrais
  // champs sont ca_month, bank_balance et unpaid_amount ; aucun champ DSO
  // n'existe nulle part côté backend.
  //
  // ca/charges : agrégés sur les 6 derniers mois réels déjà chargés
  // (monthlyRevenue) plutôt qu'un champ annuel qui n'existe pas — pas de
  // nouvel endpoint dans cette tâche. Portée honnêtement affichée comme
  // "6 derniers mois", pas présentée comme un total annuel.
  const ca = monthlyRevenue.reduce((s, d) => s + (d.ca || 0), 0);
  const charges = monthlyRevenue.reduce((s, d) => s + (d.charges || 0), 0);
  const resultat = ca - charges;
  const marge = ca > 0 ? resultat / ca : 0;
  // impayés : dashData.unpaid_amount existe réellement.
  const impayés = dashData?.unpaid_amount || 0;
  // dso : aucune donnée réelle disponible sans nouvel endpoint — null
  // (plutôt qu'un repli fixe de 28 jours) pour que l'affichage puisse
  // distinguer "vraiment 0" de "donnée indisponible".
  const dso = dashData?.average_payment_delay ?? null;
  const bankBalance = dashData?.bank_balance || 0;
  // bank_accounts : nombre de comptes bancaires actifs connectés — distingue
  // "aucun compte connecté" (solde 0 par absence de donnée) de "connecté,
  // solde réellement bas" (message Trésorerie de l'onglet Insights IA).
  const bankAccounts = dashData?.bank_accounts || 0;

  // Tendance CA réelle — comparaison des 3 premiers vs 3 derniers mois de
  // l'historique réel disponible (pas un texte fixe "en progression").
  function computeCaTrend() {
    if (monthlyRevenue.length < 2) return null;
    const half = Math.max(1, Math.floor(monthlyRevenue.length / 2));
    const firstHalf = monthlyRevenue.slice(0, half).reduce((s, d) => s + (d.ca || 0), 0) / half;
    const secondHalf = monthlyRevenue.slice(-half).reduce((s, d) => s + (d.ca || 0), 0) / half;
    if (firstHalf <= 0) return null;
    const pctChange = Math.round(((secondHalf - firstHalf) / firstHalf) * 1000) / 10;
    return { pctChange, direction: pctChange > 3 ? "hausse" : pctChange < -3 ? "baisse" : "stable" };
  }
  const caTrend = computeCaTrend();

  // Couverture de trésorerie réelle — solde bancaire / charges mensuelles
  // moyennes réelles, plutôt que l'ancien texte fixe "trésorerie saine" servi
  // à toutes les sociétés (message Insights IA #5). null si aucune charge
  // connue (division impossible) — bankAccounts distingue séparément
  // "pas de compte connecté" de "connecté, couverture calculée".
  const avgMonthlyCharges = monthlyRevenue.length > 0 ? charges / monthlyRevenue.length : 0;
  const treasuryMonths = avgMonthlyCharges > 0 ? bankBalance / avgMonthlyCharges : null;

  // Projection annuelle réelle — moyenne mensuelle réelle x 12, plutôt que
  // l'ancien calcul qui traitait `ca` comme un cumul depuis janvier (faux
  // depuis que `ca` est un cumul glissant sur les mois réels disponibles,
  // voir plus haut) x (12 / mois écoulés depuis janvier).
  const projectedAnnualCa = monthlyRevenue.length > 0 ? Math.round((ca / monthlyRevenue.length) * 12) : null;

  // P&L ratios
  const ratios = [
    { label: "Marge brute", value: pct(marge), good: marge > 0.15, tip: "Objectif > 15%" },
    { label: "Taux de charges", value: pct(ca > 0 ? charges / ca : 0), good: charges / ca < 0.8, tip: "Objectif < 80% du CA" },
    { label: "DSO (délai encaissement)", value: dso != null ? Math.round(dso) + " jours" : "Non disponible", good: dso != null && dso < 35, tip: dso != null ? "Objectif < 35 jours" : "Donnée non trackée actuellement" },
    { label: "Taux d'impayés", value: fmt(impayés) + " €", good: impayés < 5000, tip: "Objectif < 5 000 €" },
    { label: "Résultat net estimé", value: fmt(resultat) + " €", good: resultat > 0, tip: "Résultat avant IS" },
  ];

  // Sector breakdown (static mock — extend with real data if available)
  const sectors = dashData?.by_sector || [
    { name: "Services", value: Math.round(ca * 0.44) },
    { name: "SaaS / Licences", value: Math.round(ca * 0.30) },
    { name: "Distribution", value: Math.round(ca * 0.20) },
    { name: "Autre", value: Math.round(ca * 0.06) },
  ];

  // Client top 5
  const topClients = dashData?.top_clients || [];

  const TABS = [
    { id: "kpis",      label: "📊 KPIs globaux" },
    { id: "commercial",label: "💼 Commercial" },
    { id: "ratios",    label: "📐 Ratios financiers" },
    { id: "ia",        label: "📋 Synthèse" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>📊 Analytique avancé <span style={{ fontSize: 12, background: "#534ab722", color: "#a78bfa", padding: "2px 8px", borderRadius: 20, marginLeft: 8 }}>IA</span></h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete}</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...S.input, width: 90 }}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={loadDash} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>🔄</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement des données analytiques…</div>}

      {/* ── KPIs globaux ── */}
      {!loading && tab === "kpis" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <KPICard label="CA total HT" value={fmt(ca) + " €"} color="#1a9e72" />
            <KPICard label="Charges totales" value={fmt(charges) + " €"} color="#f59e0b" />
            <KPICard label="Résultat net" value={fmt(resultat) + " €"} color={resultat >= 0 ? "#1a9e72" : "#ef4444"} />
            <KPICard label="Marge nette" value={pct(marge)} color="#185fa5" sub={marge > 0.15 ? "✅ Objectif atteint" : "⚠️ Sous l'objectif"} trend={marge > 0.15 ? "up" : "down"} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>CA mensuel {year} (k€)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueWithProfit} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                  <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                  <Legend />
                  <Bar dataKey="ca" name="CA HT" fill="#1a9e72" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="charges" name="Charges" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Répartition du CA par secteur</div>
              {sectors.some(s => s.value > 0) ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={sectors} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" nameKey="name">
                      {sectors.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Données insuffisantes</div>
              )}
            </div>
          </div>

          <div style={{ ...S.card, marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Évolution du résultat mensuel</div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={revenueWithProfit} margin={{ left: 0, right: 10 }}>
                <defs>
                  <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1a9e72" stopOpacity={0.3} /><stop offset="95%" stopColor="#1a9e72" stopOpacity={0} /></linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Area type="monotone" dataKey="resultat" name="Résultat" stroke="#1a9e72" fill="url(#gr)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Commercial ── */}
      {!loading && tab === "commercial" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <KPICard label="Nouveaux clients (mois)" value={String(dashData?.new_clients_month || 0)} color="#185fa5" />
            <KPICard label="Taux de fidélisation" value={pct(dashData?.retention_rate || 0.84)} color="#1a9e72" sub="↗ +2pts vs mois dernier" trend="up" />
            <KPICard label="DSO moyen" value={dso != null ? Math.round(dso) + " jours" : "Non disponible"} color={dso != null && dso < 35 ? "#1a9e72" : "#f59e0b"} sub={dso != null ? (dso < 35 ? "Délai correct" : "Délai élevé") : "Donnée non trackée"} trend={dso != null && dso < 35 ? "up" : "down"} />
            <KPICard label="Factures impayées" value={fmt(impayés) + " €"} color={impayés > 5000 ? "#ef4444" : "#1a9e72"} />
          </div>

          {topClients.length > 0 && (
            <div style={{ ...S.card, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Top clients par CA</div>
              {topClients.slice(0, 5).map((c, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "#111827" }}>{c.name || c.client_name}</span>
                    <span style={{ color: "#1a9e72", fontWeight: 700 }}>{fmt(c.total || c.ca)} €</span>
                  </div>
                  <div style={{ height: 6, background: "#f9fafb", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: pct((c.total || c.ca) / (ca || 1)), background: COLORS[i], borderRadius: 3 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Analyse commerciale</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                caTrend ? (
                  caTrend.direction === "hausse"
                    ? { icon: "📈", type: "success", title: "CA en hausse", msg: `Le CA réel est en hausse de ${caTrend.pctChange}% entre le début et la fin de l'historique disponible (${monthlyRevenue.length} mois).` }
                    : caTrend.direction === "baisse"
                    ? { icon: "📉", type: "info", title: "CA en baisse", msg: `Le CA réel est en baisse de ${Math.abs(caTrend.pctChange)}% entre le début et la fin de l'historique disponible (${monthlyRevenue.length} mois).` }
                    : { icon: "➖", type: "info", title: "CA stable", msg: `Le CA réel est resté stable (${caTrend.pctChange >= 0 ? "+" : ""}${caTrend.pctChange}%) sur l'historique disponible (${monthlyRevenue.length} mois).` }
                ) : { icon: "ℹ️", type: "info", title: "Historique insuffisant", msg: "Pas assez de mois de données réelles pour calculer une tendance de CA." },
                impayés > 0
                  ? { icon: "⚠️", type: "info", title: "Impayés à traiter", msg: `${fmt(impayés)} € de factures impayées actuellement${dso != null && dso > 35 ? `, avec un délai moyen de règlement de ${Math.round(dso)} jours (au-dessus de l'objectif de 35 jours)` : ""}.` }
                  : { icon: "✅", type: "success", title: "Aucun impayé", msg: "Aucune facture impayée actuellement recensée." },
              ].map(a => (
                <div key={a.title} style={{ padding: 14, borderRadius: 10, background: a.type === "success" ? "rgba(26,158,114,0.1)" : "rgba(24,95,165,0.1)", border: `1px solid ${a.type === "success" ? "rgba(26,158,114,0.3)" : "rgba(24,95,165,0.3)"}` }}>
                  <div style={{ color: "#111827", fontWeight: 700, marginBottom: 4, fontSize: 13 }}>{a.icon} {a.title}</div>
                  <div style={{ color: "#4b5563", fontSize: 12 }}>{a.msg}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Ratios financiers ── */}
      {!loading && tab === "ratios" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Ratios de rentabilité</div>
              {ratios.map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <div style={{ color: "#4b5563", fontSize: 12 }}>{r.label}</div>
                    <div style={{ color: "#4b5563", fontSize: 11 }}>{r.tip}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 800, color: r.good ? "#1a9e72" : "#f59e0b", fontSize: 15 }}>{r.value}</span>
                    <span style={{ fontSize: 14 }}>{r.good ? "✅" : "⚠️"}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Compte de résultat simplifié</div>
              {[
                { label: "Chiffre d'affaires HT", value: fmt(ca) + " €", bold: true, color: "#1a9e72" },
                { label: "Achats et charges externes", value: "— " + fmt(charges * 0.4) + " €", color: "#111827" },
                { label: "Charges de personnel", value: "— " + fmt(charges * 0.45) + " €", color: "#111827" },
                { label: "Autres charges", value: "— " + fmt(charges * 0.15) + " €", color: "#111827" },
                { label: "EBE (approx.)", value: fmt(resultat * 1.3) + " €", bold: true, color: "#185fa5" },
                { label: "Dotations amortissements", value: "— " + fmt(resultat * 0.1) + " €", color: "#f59e0b" },
                { label: "Résultat courant avant IS", value: fmt(resultat) + " €", bold: true, color: resultat >= 0 ? "#1a9e72" : "#ef4444" },
              ].map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ color: r.bold ? "#111827" : "#4b5563", fontWeight: r.bold ? 700 : 400, fontSize: 12 }}>{r.label}</span>
                  <span style={{ color: r.color, fontWeight: r.bold ? 800 : 700, fontSize: 12 }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Évolution des ratios clés</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenueWithProfit.map(d => ({ ...d, marge: d.ca > 0 ? Math.round((d.resultat / d.ca) * 100) : 0 }))} margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={(v, n) => [n === "marge" ? v + "%" : fmt(v) + " €", n]} />
                <Legend />
                <Line type="monotone" dataKey="marge" name="Marge %" stroke="#1a9e72" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Synthèse — calcul réel, pas un appel IA ── */}
      {!loading && tab === "ia" && (
        <div>
          <div style={{ background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.25)", borderRadius: 12, padding: 16, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 24 }}>📋</span>
            <div>
              <div style={{ color: "#185fa5", fontWeight: 700, marginBottom: 4 }}>Synthèse calculée</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>Calculée directement à partir des données réelles de {currentSociete} ({monthlyRevenue.length} derniers mois disponibles) — pas de génération par IA. Le point DSO reste non disponible faute de donnée trackée.</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              // #1 Tendance CA — condition sur caTrend (même calcul réel que
              // l'onglet Commercial), plus l'ancien "en ligne avec les
              // objectifs annuels" affiché systématiquement sans objectif réel.
              caTrend
                ? caTrend.direction === "hausse"
                  ? { icon: "📈", color: "#1a9e72", title: "Tendance CA favorable", msg: `Le CA réel est en hausse de ${caTrend.pctChange}% entre le début et la fin des ${monthlyRevenue.length} derniers mois disponibles (${fmt(ca)} € cumulés sur la période).` }
                  : caTrend.direction === "baisse"
                  ? { icon: "📉", color: "#f59e0b", title: "Tendance CA en baisse", msg: `Le CA réel est en baisse de ${Math.abs(caTrend.pctChange)}% entre le début et la fin des ${monthlyRevenue.length} derniers mois disponibles (${fmt(ca)} € cumulés sur la période). Une analyse des causes est recommandée.` }
                  : { icon: "➖", color: "#185fa5", title: "Tendance CA stable", msg: `Le CA réel est resté stable (${caTrend.pctChange >= 0 ? "+" : ""}${caTrend.pctChange}%) sur les ${monthlyRevenue.length} derniers mois disponibles (${fmt(ca)} € cumulés sur la période).` }
                : { icon: "ℹ️", color: "#4b5563", title: "Tendance CA — historique insuffisant", msg: "Pas assez de mois de données réelles pour calculer une tendance de CA." },
              // #2 DSO — aucune donnée réelle de délai de paiement n'existe
              // côté backend (pas de champ average_payment_delay, ni de calcul
              // dérivable des données déjà chargées sur cette page). Signalé
              // honnêtement plutôt que de garder le chiffre fixe "5 à 8 jours"
              // affiché quelle que soit la société — hors périmètre de cette
              // tâche (nécessiterait un nouvel endpoint calculant le délai
              // réel facture-émise → facture-payée).
              { icon: "ℹ️", color: "#6b7280", title: "DSO — donnée non disponible", msg: "Le délai moyen de règlement clients n'est pas encore calculé par FinanceApp (aucune donnée de délai de paiement disponible sur cette page). Cette analyse nécessiterait un suivi dédié, non couvert actuellement." },
              // #3 Marge — condition sur le seuil de 15% déjà utilisé ailleurs
              // sur cette page (ratios, KPICard Marge nette).
              marge > 0.15
                ? { icon: "✅", color: "#1a9e72", title: "Marge saine", msg: `La marge nette de ${pct(marge)} dépasse l'objectif de 15%. Continuez à cibler les projets à haute valeur ajoutée pour la maintenir.` }
                : marge > 0
                ? { icon: "💡", color: "#185fa5", title: "Optimisation de la marge", msg: `La marge nette de ${pct(marge)} est sous l'objectif de 15%. Identifiez les clients avec le meilleur ratio CA/charges pour l'améliorer.` }
                : { icon: "⚠️", color: "#ef4444", title: "Marge négative", msg: `La marge nette est négative (${pct(marge)}) sur la période : les charges dépassent le CA encaissé. Une revue des charges est recommandée.` },
              // #4 Prévision annuelle — projection réelle (moyenne mensuelle
              // réelle x 12) plutôt que l'ancien calcul qui traitait `ca`
              // comme un cumul depuis janvier (faux depuis que `ca` est un
              // cumul sur les mois réels disponibles, pas l'année civile).
              projectedAnnualCa != null
                ? { icon: "🔮", color: "#8b5cf6", title: "Prévision annuelle", msg: `Sur la base de la moyenne mensuelle réelle des ${monthlyRevenue.length} derniers mois, le CA annuel projeté est d'environ ${fmt(projectedAnnualCa)} €. Consultez le module Prévisions pour les scénarios détaillés.`, action: "forecast" }
                : { icon: "ℹ️", color: "#4b5563", title: "Prévision annuelle — historique insuffisant", msg: "Pas assez de mois de données réelles pour projeter un CA annuel." },
              // #5 Trésorerie — l'exemple cité par l'utilisateur : "trésorerie
              // saine" était affiché systématiquement, y compris pour des
              // sociétés sans compte bancaire connecté (ex. Place Privée,
              // 0 ligne bank_accounts). bankAccounts distingue "pas de compte
              // connecté" (message dédié, pas de fausse évaluation) de
              // "connecté, couverture calculée en mois de charges réelles".
              bankAccounts === 0
                ? { icon: "ℹ️", color: "#6b7280", title: "Trésorerie — aucun compte connecté", msg: "Aucun compte bancaire n'est connecté à FinanceApp pour cette société : impossible d'évaluer la trésorerie réelle. Connectez un compte dans Trésorerie & Banque pour activer cette analyse." }
                : treasuryMonths == null
                ? { icon: "ℹ️", color: "#6b7280", title: "Trésorerie — donnée insuffisante", msg: `Solde bancaire de ${fmt(bankBalance)} €, mais pas assez de charges réelles sur la période pour estimer une couverture en mois.` }
                : treasuryMonths >= 3
                ? { icon: "✅", color: "#22c55e", title: "Point positif — Trésorerie", msg: `La trésorerie disponible (${fmt(bankBalance)} €) couvre environ ${treasuryMonths.toFixed(1)} mois de charges réelles. Pensez à placer les excédents en compte rémunéré ou OPCVM monétaires pour optimiser le rendement des liquidités.` }
                : treasuryMonths >= 0
                ? { icon: "⚠️", color: "#f59e0b", title: "Trésorerie à surveiller", msg: `La trésorerie disponible (${fmt(bankBalance)} €) ne couvre qu'environ ${treasuryMonths.toFixed(1)} mois de charges réelles (objectif recommandé : 3 mois). Surveillez les encaissements à venir.` }
                : { icon: "🔴", color: "#ef4444", title: "Trésorerie négative", msg: `Le solde bancaire connecté est négatif (${fmt(bankBalance)} €). Une action immédiate sur les encaissements ou le financement court terme est recommandée.` },
            ].map(a => (
              <div key={a.title} style={{ ...S.card, display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: a.color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{a.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#111827", fontWeight: 700, marginBottom: 6, fontSize: 14 }}>{a.title}</div>
                  <div style={{ color: "#4b5563", fontSize: 13 }}>{a.msg}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
